import { useCallback, useState } from 'react'

import { useAuth } from '../../lib/auth/AuthContext.tsx'
import { Link, usePath } from '../../lib/router.tsx'
import { ConfirmDialog } from '../primitives/Dialog.tsx'
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '../primitives/Menu.tsx'
import { RoleBadge } from '../primitives/Badge.tsx'
import { ThemeToggle } from '../primitives/ThemeToggle.tsx'
import { Tooltip } from '../primitives/Tooltip.tsx'
import { NAV_GROUPS, TENANT_NAV_GROUPS } from './nav.ts'
import type { NavItem } from './nav.ts'

const COLLAPSE_KEY = 'hermes.sidebar.collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * O menu lateral.
 *
 * Em telas largas é uma coluna fixa; abaixo de `lg` vira uma faixa horizontal
 * rolável no topo. Nenhuma gaveta, nenhum overlay: a navegação de sete itens
 * cabe numa faixa, e uma gaveta acrescentaria foco preso, animação e um estado
 * a mais para manter — tudo para esconder o que já cabia.
 *
 * O modo recolhido existe porque o console fica num monitor secundário: a grade
 * do dashboard aproveita cada pixel de largura, e quem passa o dia nele já sabe
 * o que é `LC`. A dica de ferramenta cobre quem não sabe.
 */
export function Sidebar() {
  const path = usePath()
  const { user, isTenant, isPlatformAdmin, isTenantAdmin, companyName, signOut, signOutEverywhere } =
    useAuth()
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [confirmAll, setConfirmAll] = useState(false)
  const [busy, setBusy] = useState(false)

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current
      try {
        localStorage.setItem(COLLAPSE_KEY, String(next))
      } catch {
        // Preferência perdida no reload; a sessão ainda a respeita.
      }
      return next
    })
  }, [])

  // Um menu por experiência, escolhida pelo `scope` de /auth/me.
  const groups = (isTenant ? TENANT_NAV_GROUPS : NAV_GROUPS).map((group) => ({
    ...group,
    // A tela de usuários some inteira para quem não é administrador. Esconder o
    // item é conveniência: a rota barra de novo, e a API responde 403 de todo
    // jeito para quem chamar na mão.
    items: group.items.filter(
      (item) =>
        item.requires === undefined ||
        (item.requires === 'platformAdmin' && isPlatformAdmin) ||
        (item.requires === 'tenantAdmin' && isTenantAdmin),
    ),
  })).filter((group) => group.items.length > 0)

  // "Black Ghost" é só rótulo do escopo `platform` — não existe empresa 0 (§0.1).
  const orgLabel = isTenant ? (companyName ?? 'empresa') : 'Black Ghost'

  return (
    <>
      <div
        className={`flex shrink-0 flex-col border-line bg-surface max-lg:border-b lg:sticky lg:top-0 lg:h-screen lg:border-r ${
          collapsed ? 'lg:w-13' : 'lg:w-52'
        }`}
      >
        <div className="flex items-center gap-2 border-line-subtle px-3 py-1.5 lg:border-b">
          <Link
            href="/"
            className="font-mono text-base font-medium tracking-label text-fg no-underline"
            aria-label={isTenant ? `Hermes — ${orgLabel}` : 'Hermes — console da Black Ghost'}
          >
            {collapsed ? 'H' : 'HERMES'}
          </Link>
          {!collapsed && (
            <span
              className="min-w-0 truncate text-micro uppercase tracking-label text-fg-muted"
              title={orgLabel}
            >
              {orgLabel}
            </span>
          )}

          <button
            type="button"
            onClick={toggle}
            aria-pressed={collapsed}
            title={collapsed ? 'Expandir o menu' : 'Recolher o menu'}
            aria-label={collapsed ? 'Expandir o menu' : 'Recolher o menu'}
            className="ml-auto hidden shrink-0 border border-line-strong px-1 py-px text-micro text-fg-muted hover:bg-inset hover:text-fg lg:block"
          >
            {collapsed ? '»' : '«'}
          </button>

          {/* Na faixa horizontal o menu do usuário acompanha o wordmark: o rodapé
              da coluna não existe nesse arranjo. */}
          <div className="ml-auto flex items-center gap-2 lg:hidden">
            <ThemeToggle />
            <UserMenu onSignOut={signOut} onSignOutAll={() => setConfirmAll(true)} />
          </div>
        </div>

        <nav
          aria-label={isTenant ? 'Seções do painel' : 'Seções do console'}
          className="flex min-h-0 flex-1 gap-x-1 overflow-x-auto px-2 py-1.5 max-lg:items-center lg:flex-col lg:gap-y-3 lg:overflow-x-visible lg:overflow-y-auto"
        >
          {groups.map((group) => (
            <div key={group.label} className="flex shrink-0 gap-1 lg:flex-col lg:gap-0.5">
              {!collapsed && (
                <p className="hidden px-1 pb-0.5 text-micro uppercase tracking-label text-fg-muted lg:block">
                  {group.label}
                </p>
              )}
              {group.items.map((item) => (
                <NavLink
                  key={item.href}
                  item={item}
                  active={path === item.href}
                  collapsed={collapsed}
                />
              ))}
            </div>
          ))}
        </nav>

        <div className="hidden shrink-0 flex-col gap-2 border-t border-line-subtle px-2 py-2 lg:flex">
          {!collapsed && user !== null && (
            <div className="min-w-0 px-1">
              <p className="truncate text-sm text-fg" title={user.email}>
                {user.displayName}
              </p>
              <p className="truncate font-mono text-micro text-fg-muted">{user.username}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {user.roles.map((role) => (
                  <RoleBadge key={role} role={role} />
                ))}
              </div>
            </div>
          )}
          <div className={`flex gap-1 ${collapsed ? 'flex-col items-center' : 'items-center'}`}>
            {!collapsed && <ThemeToggle />}
            <UserMenu onSignOut={signOut} onSignOutAll={() => setConfirmAll(true)} />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title="Encerrar todas as sessões"
        consequence="Todos os dispositivos são desconectados, inclusive este."
        confirmLabel="Encerrar todas"
        busy={busy}
        onConfirm={() => {
          setBusy(true)
          void signOutEverywhere().finally(() => setBusy(false))
        }}
      />
    </>
  )
}

function NavLink({
  item,
  active,
  collapsed,
}: {
  item: NavItem
  active: boolean
  collapsed: boolean
}) {
  const link = (
    <Link
      href={item.href}
      aria-current={active ? 'page' : undefined}
      className={`flex shrink-0 items-center gap-2 border-l-2 px-2 py-1 text-sm no-underline ${
        active
          ? 'border-accent bg-inset text-fg'
          : 'border-transparent text-fg-secondary hover:bg-inset hover:text-fg'
      } ${collapsed ? 'lg:justify-center lg:px-1' : ''}`}
    >
      <span className="font-mono text-micro text-fg-muted lg:inline">{item.code}</span>
      <span className={collapsed ? 'lg:hidden' : ''}>{item.label}</span>
    </Link>
  )

  // Recolhido, o rótulo não está na tela: a dica é a única fonte do nome.
  return collapsed ? (
    <Tooltip side="right" content={item.label}>
      {link}
    </Tooltip>
  ) : (
    link
  )
}

function UserMenu({
  onSignOut,
  onSignOutAll,
}: {
  onSignOut: () => Promise<void>
  onSignOutAll: () => void
}) {
  const { user } = useAuth()

  return (
    <Menu trigger={<span>conta</span>} label="Menu da conta" align="start">
      {user !== null && <MenuLabel>{user.username}</MenuLabel>}
      <MenuItem onSelect={() => void onSignOut()} danger>
        Sair
      </MenuItem>
      <MenuSeparator />
      <MenuItem onSelect={onSignOutAll} danger>
        Encerrar todas as sessões
      </MenuItem>
    </Menu>
  )
}
